import React from 'react';
import answerChangelog from './answer-call.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { AnswerNodeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../..';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { googleLanguageOptions, telnyxLanguageOptions } from '../TranscriptionStart/languages';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { AnchorType } from '@components/Flow';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Answer',
  identifier: 'AnswerCall',
  command_id: 'call.answered',
  icon: 'telnyx',
  background: '#000000',
  changelog: answerChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.answered.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.answered.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.answered',
      label: 'call.answered',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.answered.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.transcription',
      label: 'call.transcription',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.transcription.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.started',
      label: 'streaming.started',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.started.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.stopped',
      label: 'streaming.stopped',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.stopped.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.failed',
      label: 'streaming.failed',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.failed.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.answered',
      expected: true,
      multi: false,
    },
    {
      webhook: 'streaming.started',
      expected: false,
      multi: false,
    },
    {
      webhook: 'streaming.stopped',
      expected: false,
      multi: false,
    },
    {
      webhook: 'streaming.failed',
      expected: false,
      multi: false,
    },
  ],
};

export const state: AnswerNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  billing_group_id: { type: NodeInputType.Expression, value: '' },
  send_silence_when_idle: { type: NodeInputType.Option, value: 'false' },
  client_state: { type: NodeInputType.Expression, value: '' },
  preferred_codecs: [],
  // streaming
  stream_url: { type: NodeInputType.Expression, value: '' },
  stream_track: 'inbound_track',
  stream_bidirectional_mode: 'mp3',
  stream_bidirectional_codec: 'PCMU',
  stream_bidirectional_target_legs: 'opposite',
  // transcription
  transcription: { type: NodeInputType.Option, value: 'false' },
  transcription_engine: { type: NodeInputType.Option, value: 'A' },
  language_telnyx: 'en',
  language_google: 'en',
  transcription_tracks: { type: NodeInputType.Option, value: 'inbound' },
  interrupt_playback: { type: NodeInputType.Option, value: 'false' },
  interim_results: { type: NodeInputType.Option, value: 'false' },
  // headers
  custom_headers: { type: NodeInputType.CallHeader, value: [] },
  sip_headers: { type: NodeInputType.CallHeader, value: [] },
  // sound modifications
  sound_modifications_enabled: 'false',
  pitch: { type: NodeInputType.Number, value: '1' },
  semitone: { type: NodeInputType.Number, value: '0' },
  octaves: { type: NodeInputType.Number, value: '0' },
  track: 'outbound',
  // recording disabled as recording_id is not returned by API request to answer call
  // record: 'false',
  // record_format: 'mp3',
  // record_channels: 'dual',
  // record_play_beep: 'true',
  // record_custom_file_name: { type: NodeInputType.Expression, value: '' },
  // record_max_length: { type: NodeInputType.Number, value: '0' },
  // record_timeout_secs: { type: NodeInputType.Number, value: '0' },
  // record_track: 'both',
  // record_trim: 'disabled',
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'billing_group_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Billing Group ID',
    description:
      'Use this field to set the Billing Group ID for the call. Must be a valid and existing Billing Group ID.',
    placeholder: 'uuid',
    required: false,
  },
  {
    id: 'send_silence_when_idle',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Send Silence When Idle',
    description: 'Generate silence RTP packets when no transmission available.',
    required: false,
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
  {
    id: 'preferred_codecs',
    tab: '1',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    label: 'Preferred Codecs',
    placeholder: 'Set your preferred codecs for calls in order of priority. ',
    description:
      "This is the list of codecs for the forked media to be received. Codecs are selected from left to right during negotiation with the caller, and a mismatch with the caller's supported codecs may prevent the call from connecting. Always consider including fallback codecs for maximum compatibility.",
    multiSelect: true,
    options: [
      { value: 'G722', label: 'G722' },
      { value: 'PCMU', label: 'PCMU' },
      { value: 'PCMA', label: 'PCMA' },
      { value: 'G729', label: 'G729' },
      { value: 'OPUS', label: 'OPUS' },
      { value: 'VP8', label: 'VP8' },
      { value: 'H264', label: 'H264' },
    ],
    required: false,
  },
  {
    id: 'stream_url',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Stream URL',
    description: 'The destination WebSocket address where the stream is going to be delivered.',
    placeholder: 'wss://example.com',
    required: false,
  },
  {
    id: 'stream_track',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'inbound_track', label: 'Inbound Track' },
      { value: 'outbound_track', label: 'Outbound Track' },
      { value: 'both_tracks', label: 'Both Tracks' },
    ],
    label: 'Stream Track',
    description: 'Specifies which track should be streamed.',
    required: false,
  },
  {
    id: 'stream_bidirectional_mode',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'mp3', label: 'MP3' },
      { value: 'rtp', label: 'RTP' },
    ],
    label: 'Stream Bidirectional Mode',
    description: 'Configures method of bidirectional streaming (mp3, rtp).',
    required: false,
  },
  {
    id: 'stream_bidirectional_codec',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'PCMU', label: 'PCMU' },
      { value: 'PCMA', label: 'PCMA' },
      { value: 'G722', label: 'G722' },
    ],
    label: 'Stream Bidirectional Codec',
    description: 'Indicates codec for bidirectional streaming RTP payloads.',
    required: false,
  },
  {
    id: 'stream_bidirectional_target_legs',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'opposite', label: 'Opposite' },
      { value: 'both', label: 'Both' },
      { value: 'self', label: 'Self' },
    ],
    label: 'Stream Bidirectional Target Legs',
    description: 'Specifies which call legs should receive the bidirectional stream audio.',
    required: false,
  },
  {
    id: 'transcription',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Transcription',
    description: 'Enable transcription upon call answer. The default value is false.',
    required: false,
  },
  {
    id: 'language_telnyx',
    type: NodeFieldType.Option,
    options: telnyxLanguageOptions,
    label: 'Telnyx Transcription Language',
    description: 'Language to use for speech recognition.',
    required: false,
    placeholder: 'Language to use for speech recognition, default English.',
  },
  {
    id: 'language_google',
    type: NodeFieldType.Option,
    options: googleLanguageOptions,
    label: 'Google Transcription Language',
    description: 'Language to use for speech recognition.',
    required: false,
    placeholder: 'Language to use for speech recognition, default English.',
  },
  {
    id: 'transcription_tracks',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Transcription Tracks',
    description:
      'Indicates which leg of the call will be transcribed. Use inbound for the leg that requested the transcription, outbound for the other leg, and both for both legs of the call. Will default to inbound.',
    required: false,
    options: [
      { label: 'Inbound', value: 'inbound' },
      { label: 'Outbound', value: 'outbound' },
      { label: 'Both', value: 'both' },
    ],
  },
  {
    id: 'interrupt_playback',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    options: [
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
    label: 'Interrupt Playback',
    description:
      'Only available with Google transcription engine. When enabled, any playback actively running on the call will be stopped immediately when a new transcription is received to this node. You can use this to implement interruptions when the end user speaks over an actively running playback.',
    required: false,
  },
  {
    id: 'interim_results',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Interim Results',
    description:
      'Whether to send interim results. If set to false, only final results will be sent. Applies to google engine only.',
    required: false,
    placeholder: 'If set to false, only final results will be sent.',
  },
  {
    id: 'transcription_engine',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Transcription Engine',
    description: 'Engine to use for speech recognition.',
    required: false,
    options: [
      { label: 'Google', value: 'A' },
      { label: 'Telnyx', value: 'B' },
    ],
  },
  {
    id: 'custom_headers',
    type: NodeFieldType.CallHeaders,
    label: 'Custom Headers',
    description: 'Custom headers to be added to the SIP INVITE response.',
    placeholder: 'A formatted array of headers.',
    required: false,
  },
  {
    id: 'sip_headers',
    type: NodeFieldType.CallHeaders,
    label: 'SIP Headers',
    description: 'SIP headers to be added to the SIP INVITE response. Currently only User-to-User header is supported.',
    placeholder: 'A formatted array of headers.',
    required: false,
  },
  {
    id: 'sound_modifications_enabled',
    type: NodeFieldType.Toggle,
    label: 'Sound Modifications',
    description: 'Use this field to modify sound effects, for example adjust the pitch.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'pitch',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Pitch',
    description: 'Set the pitch directly, value should be > 0, default 1 (lower = lower tone).',
    placeholder: 'Set the pitch directly, value should be > 0.',
    required: false,
    minNumber: 0,
  },
  {
    id: 'semitone',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Semitone',
    description: 'Adjust the pitch in semitones, values should be between -14 and 14, default 0.',
    placeholder: 'Adjust the pitch in semitones, values should be between -14 and 14.',
    required: false,
    maxNumber: 14,
    minNumber: -14,
  },
  {
    id: 'octaves',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Octaves',
    description: 'Adjust the pitch in octaves, values should be between -1 and 1, default 0.',
    placeholder: 'Adjust the pitch in octaves, values should be between -1 and 1.',
    required: false,
    maxNumber: 1,
    minNumber: -1,
  },
  {
    id: 'track',
    tab: 'sound_modifications',
    type: NodeFieldType.Option,
    label: 'Track',
    description: 'The track to which the sound modifications will be applied.',
    options: [
      { label: 'Outbound', value: 'outbound' },
      { label: 'Inbound', value: 'inbound' },
    ],
    required: false,
  },
  // {
  //   id: 'record',
  //   tab: '3',
  //   type: NodeFieldType.Toggle,
  //   label: 'Record Call',
  //   description: 'Start recording as soon as the call is answered.',
  //   options: [
  //     { label: 'Enabled', value: 'record-from-answer' },
  //     { label: 'Disabled', value: 'false' },
  //   ],
  //   required: false,
  // },
  // {
  //   id: 'record_format',
  //   tab: '3',
  //   type: NodeFieldType.Toggle,
  //   label: 'Recording Format',
  //   description: 'The audio file format used when storing the call recording.',
  //   options: [
  //     { label: 'MP3', value: 'mp3' },
  //     { label: 'WAV', value: 'wav' },
  //   ],
  //   required: true,
  // },
  // {
  //   id: 'record_channels',
  //   tab: '3',
  //   type: NodeFieldType.Toggle,
  //   label: 'Recording Channels',
  //   description:
  //     'When dual, final audio file will be stereo recorded with the first leg on channel A, and the rest on channel B.',
  //   options: [
  //     { label: 'Single', value: 'single' },
  //     { label: 'Dual', value: 'dual' },
  //   ],
  //   required: false,
  // },
  // {
  //   id: 'record_custom_file_name',
  //   tab: '3',
  //   type: NodeFieldType.InputExpression,
  //   label: 'Recording File Name',
  //   description:
  //     'The custom recording file name to be used instead of the default call_leg_id. Telnyx will still add a Unix timestamp suffix. Must be less than 40 characters.',
  //   placeholder: 'Give your recording file a name (max 40 characters).',
  //   required: false,
  // },
  // {
  //   id: 'record_track',
  //   tab: '3',
  //   type: NodeFieldType.Option,
  //   label: 'Recording Track',
  //   description:
  //     'The audio track to be recorded. Can be either both, inbound or outbound. If only single track is specified (inbound, outbound), channels configuration is ignored and it will be recorded as mono (single channel).',
  //   options: [
  //     { label: 'Both', value: 'both' },
  //     { label: 'Inbound', value: 'inbound' },
  //     { label: 'Outbound', value: 'outbound' },
  //   ],
  //   required: false,
  // },
  // {
  //   id: 'record_trim',
  //   tab: '3',
  //   type: NodeFieldType.Option,
  //   label: 'Trim Recording',
  //   description: 'When set to trim-silence, silence will be removed from the beginning and end of the recording.',
  //   options: [
  //     { label: 'Disabled', value: 'disabled' },
  //     { label: 'Trim Silence', value: 'trim-silence' },
  //   ],
  //   required: false,
  // },
  // {
  //   id: 'record_max_length',
  //   tab: '3',
  //   type: NodeFieldType.NumberExpression,
  //   label: 'Max Recording Length',
  //   description:
  //     'Defines the maximum length for the recording in seconds. The minimum value is 0. The maximum value is 14400. The default value is 0 (infinite)',
  //   placeholder: 'Define the max recording length. 0 is infinite.',
  //   required: false,
  //   minNumber: 0,
  //   maxNumber: 14400,
  // },
  // {
  //   id: 'record_timeout_secs',
  //   tab: '3',
  //   type: NodeFieldType.NumberExpression,
  //   label: 'Recording Timeout (secs)',
  //   description:
  //     'The number of seconds that Telnyx will wait for the recording to be stopped if silence is detected. The timer only starts when the speech is detected. Please note that call transcription is used to detect silence and the related charge will be applied. The minimum value is 0. The default value is 0 (infinite)',
  //   placeholder: 'Define silence duration before terminating recording. 0 is infinite.',
  //   required: false,
  //   minNumber: 0,
  // },
];

export const output: NodeOutput = {
  default: {
    'call.answered': {
      call: {
        answered: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          start_time: 'string',
          from: 'string',
          to: 'string',
          state: 'string',
        },
      },
    },
    'call.transcription': {
      call: {
        transcription: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          transcription_data: {
            confidence: 'number',
            is_final: 'true | false',
            transcript: 'string',
          },
        },
      },
    },
    'streaming.started': {
      streaming: {
        started: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          stream_id: 'string',
          stream_params: {
            stream_url: 'string',
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
    'streaming.stopped': {
      streaming: {
        stopped: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          stream_id: 'string',
          stream_params: {
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
            stream_url: 'string',
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
    'streaming.failed': {
      streaming: {
        failed: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          failure_reason: 'string',
          stream_id: 'string',
          stream_params: {
            stream_url: 'string',
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
  },
  dynamic: null,
};

export const AnswerCall: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.answered') return;

  const state = command.state as AnswerNodeState;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={getAnswerAnchors(anchors, state)} />
    </NodeContainer>
  );
};

const getAnswerAnchors = (anchors: AnchorType[], state: AnswerNodeState) => {
  const isTranscriptionEnabled = state?.transcription?.value === 'true';
  const isStreamingEnabled = Boolean(state?.stream_url?.value);

  return anchors.filter((anchor) => {
    if (!isTranscriptionEnabled && anchor.id.includes('transcription')) return false;
    if (!isStreamingEnabled && anchor.id.includes('streaming.')) return false;
    return true;
  });
};

export default { config, state, fields, output };
